/**
 * A page head's Refresh: refetches the page's queries now, without losing the
 * filters, sort or page in view. Only queries on screen are refetched; the
 * rest are marked stale and load fresh when next shown. `iconOnly` is the
 * filter toolbar's square icon button; the page head keeps the labelled one.
 */
import { useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';

export function RefreshButton({ queryKeys, iconOnly = false }: { queryKeys: QueryKey[]; iconOnly?: boolean }) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setBusy(true);
    try {
      await Promise.all(queryKeys.map((queryKey) => client.invalidateQueries({ queryKey })));
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={`btn btn-secondary${iconOnly ? ' btn-icon' : ''}`}
      onClick={() => void refresh()}
      disabled={busy}
      aria-busy={busy}
      aria-label={iconOnly ? 'Refresh' : undefined}
      title="Load the latest data"
    >
      <RotateCw aria-hidden="true" className={busy ? 'spin' : undefined} />
      {!iconOnly && ' Refresh'}
    </button>
  );
}
